import { Navigate, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { useAuthStore, selectIsAuthenticated, selectHasHydrated } from "@/stores/auth.store";
import type { UserRole } from "@/types/api";

interface ProtectedRouteProps {
  children: ReactNode;
  roles?: UserRole[];
}

export function ProtectedRoute({ children, roles }: ProtectedRouteProps): JSX.Element {
  const hasHydrated = useAuthStore(selectHasHydrated);
  const isAuth = useAuthStore(selectIsAuthenticated);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  // Wait for the persisted store to rehydrate before deciding auth state.
  if (!hasHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-nav-softer">
        <Loader2 className="h-6 w-6 text-nav-teal animate-spin" aria-label="Loading" />
      </div>
    );
  }

  if (!isAuth) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (roles && user && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
